// src/edu-world/systems/npc-renderer.ts
// NPC rendering using real PNG assets with scale (Phase 1)

import Phaser from 'phaser';
import type { NPCConfig } from '../types';

export interface NPCSprite {
  id: string;
  config: NPCConfig;
  body: Phaser.GameObjects.Image;
  indicator: Phaser.GameObjects.Container | null;
  proximityIndicator: Phaser.GameObjects.Container | null;
}

export class NPCRenderer {
  private scene: Phaser.Scene;
  private npcs: Map<string, NPCSprite> = new Map();

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /** Render an NPC with real sprite */
  addNPC(config: NPCConfig, hasMission: boolean): NPCSprite {
    const { x, y } = config.position;

    // ── NPC image sprite ──
    const textureKey = config.id === 'rosa' ? 'rosa' : config.id === 'tome' ? 'tome' : 'player';
    const body = this.scene.add.image(x, y, textureKey);
    body.setDepth(5);

    // Scale based on character type — reduced for 1536px map
    if (config.id === 'rosa') {
      body.setScale(0.025); // ~31px from 1254
    } else if (config.id === 'tome') {
      body.setScale(0.012); // ~18px from 1512 (small bird)
    } else {
      body.setScale(0.022); // professor and others
    }

    // ── Gentle idle animation ──
    this.scene.tweens.add({
      targets: body,
      y: y - 3,
      duration: 1500,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // ── Name label ──
    const labelText = config.id === 'rosa' ? '🌸 Rosa' : config.id === 'tome' ? '🐦 Tomé' : config.name;
    const label = this.scene.add.text(x, y - 22, labelText, {
      fontSize: '7px',
      fontFamily: 'sans-serif',
      color: config.id === 'rosa' ? '#c2185b' : '#e65100',
      fontStyle: 'bold',
      backgroundColor: '#ffffffcc',
      padding: { x: 4, y: 2 },
    }).setOrigin(0.5).setDepth(6);
    label.setData('npcOwner', config.id);
    label.setData('offsetY', -22);

    // ── Mission-available indicator ──
    let indicator: Phaser.GameObjects.Container | null = null;
    if (hasMission) {
      indicator = this.createMissionIndicator(config);
    }

    // ── Proximity indicator ──
    const proximityIndicator = this.createProximityIndicator(config);

    const npcSprite: NPCSprite = { id: config.id, config, body, indicator, proximityIndicator };
    this.npcs.set(config.id, npcSprite);
    return npcSprite;
  }

  private createMissionIndicator(config: NPCConfig): Phaser.GameObjects.Container {
    const { x, y } = config.position;
    const container = this.scene.add.container(x, y - 32);
    container.setDepth(8);
    container.setData('npcOwner', config.id);
    container.setData('offsetY', -32);

    const bubble = this.scene.add.ellipse(0, 0, 14, 12, 0xfff9c4);
    bubble.setStrokeStyle(1, 0xf9a825);
    const excl = this.scene.add.text(0, -1, '❗', { fontSize: '7px' }).setOrigin(0.5);
    container.add([bubble, excl]);

    this.scene.tweens.add({
      targets: container,
      scale: { from: 0.85, to: 1.1 },
      alpha: { from: 0.7, to: 1 },
      duration: 700,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    return container;
  }

  private createProximityIndicator(config: NPCConfig): Phaser.GameObjects.Container {
    const { x, y } = config.position;
    const container = this.scene.add.container(x, y - 24);
    container.setDepth(9);
    container.setData('npcOwner', config.id);
    container.setData('offsetY', -24);

    const ring = this.scene.add.circle(0, 0, 8);
    ring.setStrokeStyle(1.5, 0x4fc3f7);
    ring.setFillStyle(0x4fc3f7, 0.2);
    const star = this.scene.add.text(0, 0, '✨', { fontSize: '8px' }).setOrigin(0.5);
    container.add([ring, star]);
    container.setAlpha(0);
    container.setScale(0);

    return container;
  }

  showProximityIndicator(npcId: string): void {
    const npc = this.npcs.get(npcId);
    if (!npc || !npc.proximityIndicator) return;
    this.scene.tweens.add({
      targets: npc.proximityIndicator,
      alpha: 1, scale: 1,
      duration: 250,
      ease: 'Back.easeOut',
    });
    // Hide mission indicator when proximity shows (avoid overlap)
    if (npc.indicator) {
      this.scene.tweens.add({
        targets: npc.indicator,
        alpha: 0,
        duration: 200,
      });
    }
  }

  hideProximityIndicator(npcId: string): void {
    const npc = this.npcs.get(npcId);
    if (!npc || !npc.proximityIndicator) return;
    this.scene.tweens.add({
      targets: npc.proximityIndicator,
      alpha: 0, scale: 0,
      duration: 400,
      ease: 'Sine.easeIn',
    });
    // Show mission indicator again when proximity hides
    if (npc.indicator) {
      this.scene.tweens.add({
        targets: npc.indicator,
        alpha: 1,
        duration: 300,
        delay: 400,
      });
    }
  }

  playBeckonAnimation(npcId: string): void {
    const npc = this.npcs.get(npcId);
    if (!npc) return;
    this.scene.tweens.add({
      targets: npc.body,
      x: npc.body.x + 8,
      duration: 150,
      yoyo: true,
      repeat: 2,
      ease: 'Sine.easeInOut',
    });
  }

  playJumpHappy(npcId: string): void {
    const npc = this.npcs.get(npcId);
    if (!npc) return;
    this.scene.tweens.add({
      targets: npc.body,
      y: npc.body.y - 30,
      duration: 300,
      yoyo: true,
      repeat: 2,
      ease: 'Bounce.easeOut',
    });
  }

  playAcceptAnimation(npcId: string): void {
    const npc = this.npcs.get(npcId);
    if (!npc) return;
    this.scene.tweens.add({
      targets: npc.body,
      scaleX: (npc.body.scale) * 1.15,
      scaleY: (npc.body.scale) * 0.9,
      duration: 150,
      yoyo: true,
      repeat: 1,
      ease: 'Sine.easeInOut',
    });
  }

  hideMissionIndicator(npcId: string): void {
    const npc = this.npcs.get(npcId);
    if (!npc || !npc.indicator) return;
    this.scene.tweens.add({
      targets: npc.indicator,
      alpha: 0, scale: 0,
      duration: 300,
      onComplete: () => {
        npc.indicator?.destroy();
        npc.indicator = null;
      },
    });
  }

  getNPC(npcId: string): NPCSprite | undefined {
    return this.npcs.get(npcId);
  }

  getAllNPCs(): NPCSprite[] {
    return Array.from(this.npcs.values());
  }
}
